/**
 * Landing-page line icons — inline SVG, 24x24 grid, 1.75px stroke, currentColor.
 * No icon library is installed in the PWA, so this small set keeps the landing
 * page dependency-free. Every icon is decorative (aria-hidden) unless a parent
 * supplies its own label.
 */
import type { ReactElement, SVGProps } from "react";

export type IconName =
  | "file"
  | "image"
  | "video"
  | "lock"
  | "chain"
  | "clock"
  | "shield"
  | "check"
  | "fingerprint"
  | "upload"
  | "seal"
  | "search"
  | "home"
  | "alert"
  | "clipboard"
  | "flag"
  | "handshake"
  | "layers"
  | "menu"
  | "close"
  | "chevron"
  | "arrowRight"
  | "arrowDown"
  | "noCloud"
  | "eye";

type IconProps = Omit<SVGProps<SVGSVGElement>, "name"> & {
  name: IconName;
  size?: number;
};

const PATHS: Record<IconName, ReactElement> = {
  file: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
    </>
  ),
  image: (
    <>
      <rect x="4" y="5" width="16" height="14" rx="2" />
      <circle cx="9" cy="10" r="1.5" />
      <path d="M20 15.5 15.5 11 7 19" />
    </>
  ),
  video: (
    <>
      <rect x="3" y="7" width="13" height="10" rx="2" />
      <path d="m16 11 5-3v8l-5-3" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
      <circle cx="12" cy="16" r="1.25" />
    </>
  ),
  chain: (
    <>
      <path d="M10 14a3.5 3.5 0 0 0 4.95 0l2.5-2.5a3.5 3.5 0 0 0-4.95-4.95L11.5 7.5" />
      <path d="M14 10a3.5 3.5 0 0 0-4.95 0l-2.5 2.5a3.5 3.5 0 0 0 4.95 4.95l1-1" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3.5 5 6.2v5.3c0 4.2 2.9 7.6 7 9 4.1-1.4 7-4.8 7-9V6.2z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
  fingerprint: (
    <>
      <path d="M6.5 8.5A6.5 6.5 0 0 1 18.5 11c0 3-.4 5.5-1.2 7.5" />
      <path d="M9 11a3 3 0 0 1 6 0c0 3.2-.5 6-1.4 8.5" />
      <path d="M12 11c0 3.4-.6 6.4-1.8 9" />
      <path d="M5.2 12.4c.2 2.7-.1 5-.9 6.8" />
      <path d="M8 14.6c0 1.9-.3 3.6-.9 5.1" />
    </>
  ),
  upload: (
    <>
      <path d="M12 16V5" />
      <path d="m7.5 9.5 4.5-4.5 4.5 4.5" />
      <path d="M5 16v2.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V16" />
    </>
  ),
  seal: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <circle cx="12" cy="14.5" r="2.5" />
      <path d="M12 17v3" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="m20 20-4.5-4.5" />
    </>
  ),
  home: (
    <>
      <path d="m4 11 8-7 8 7" />
      <path d="M6 10v10h12V10" />
      <path d="M10 20v-6h4v6" />
    </>
  ),
  alert: (
    <>
      <path d="M12 4 3.5 19h17z" />
      <path d="M12 10v4" />
      <path d="M12 17h.01" />
    </>
  ),
  clipboard: (
    <>
      <rect x="6" y="5" width="12" height="16" rx="2" />
      <path d="M9 5a3 3 0 0 1 6 0" />
      <path d="M9 12h6M9 16h4" />
    </>
  ),
  flag: (
    <>
      <path d="M5 21V4" />
      <path d="M5 4h11l-1.5 3.5L16 11H5" />
    </>
  ),
  handshake: (
    <>
      <path d="m3 9 4-3 5 3 5-3 4 3" />
      <path d="M7 6v7l5 5 5-5V6" />
      <path d="m9 13 3 3 3-3" />
    </>
  ),
  layers: (
    <>
      <path d="m12 4 8 4-8 4-8-4z" />
      <path d="m4 12 8 4 8-4" />
      <path d="m4 16 8 4 8-4" />
    </>
  ),
  menu: (
    <>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </>
  ),
  close: (
    <>
      <path d="m6 6 12 12" />
      <path d="m18 6-12 12" />
    </>
  ),
  chevron: <path d="m6 9 6 6 6-6" />,
  arrowRight: (
    <>
      <path d="M4 12h16" />
      <path d="m14 6 6 6-6 6" />
    </>
  ),
  arrowDown: (
    <>
      <path d="M12 4v16" />
      <path d="m6 14 6 6 6-6" />
    </>
  ),
  noCloud: (
    <>
      <path d="M7.5 18.5h9a4 4 0 0 0 .8-7.9A5.5 5.5 0 0 0 7 9.2 4.5 4.5 0 0 0 7.5 18.5z" />
      <path d="m4 4 16 16" />
    </>
  ),
  eye: (
    <>
      <path d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z" />
      <circle cx="12" cy="12" r="2.5" />
    </>
  ),
};

export function Icon({ name, size = 24, className = "", ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
